import { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { OrganizationDetails, OrganizationUser } from '../types/api.types';
import { useAuth } from '../contexts/AuthContext';
import UserForm from './UserForm';

export default function OrgPanel() {
  const { user, refreshUser } = useAuth();
  const [organization, setOrganization] = useState<OrganizationDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditingOrg, setIsEditingOrg] = useState(false);
  const [editedOrgName, setEditedOrgName] = useState('');
  const [isSavingOrg, setIsSavingOrg] = useState(false);
  const [orgEditError, setOrgEditError] = useState('');

  // Check if current user is an organization admin
  const isOrgAdmin = user?.current_organization?.role?.toLowerCase() === 'admin';

  // Users section state
  const [users, setUsers] = useState<OrganizationUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState<OrganizationUser | undefined>(undefined);

  // Search/filter state
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const fetchOrganization = async () => {
    if (!user?.current_organization?.id) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const org = await apiService.getOrganization(user.current_organization.id);
      setOrganization(org);
      setEditedOrgName(org.name);
    } catch (err) {
      setError(apiService.getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    if (!user?.current_organization?.id) {
      return;
    }

    setIsLoadingUsers(true);
    setUsersError('');

    try {
      const orgUsers = await apiService.getOrganizationUsers(user.current_organization.id);
      setUsers(orgUsers);
    } catch (err) {
      setUsersError(apiService.getErrorMessage(err));
    } finally {
      setIsLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchOrganization();
    fetchUsers();
  }, [user?.current_organization?.id]);

  const handleSaveOrgName = async () => {
    if (!editedOrgName.trim()) {
      setOrgEditError('Name is required');
      return;
    }

    if (!organization) return;

    setIsSavingOrg(true);
    setOrgEditError('');

    try {
      await apiService.updateOrganization(organization.id, {
        name: editedOrgName.trim(),
      });
      setIsEditingOrg(false);
      fetchOrganization();
      refreshUser();
    } catch (err) {
      setOrgEditError(apiService.getErrorMessage(err));
    } finally {
      setIsSavingOrg(false);
    }
  };

  const handleUserFormSuccess = () => {
    setShowUserForm(false);
    setEditingUser(undefined);
    fetchUsers();
    fetchOrganization(); // Refresh user counts
  };

  const handleUserClick = (orgUser: OrganizationUser) => {
    setEditingUser(orgUser);
    setShowUserForm(true);
  };

  // Filter users based on search and filters
  const filteredUsers = users.filter(u => {
    // Search filter
    const searchLower = search.toLowerCase();
    const matchesSearch = !search ||
      u.username.toLowerCase().includes(searchLower) ||
      u.email.toLowerCase().includes(searchLower) ||
      (u.full_name?.toLowerCase().includes(searchLower) ?? false);

    // Role filter (organization_role)
    const matchesRole = roleFilter === 'all' ||
      (roleFilter === 'admin' && u.organization_role?.toLowerCase() === 'admin') ||
      (roleFilter === 'member' && u.organization_role?.toLowerCase() !== 'admin');

    // Status filter
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && u.is_active) ||
      (statusFilter === 'inactive' && !u.is_active);

    return matchesSearch && matchesRole && matchesStatus;
  });

  if (isLoading) {
    return (
      <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
        Loading organization...
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
        No organization found
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Manage Organization &amp; Users</h2>
        <p className="text-sm text-gray-500">View your organization and manage its members.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
      {/* Left Column: Organization Details */}
      <div>
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="flex justify-between items-center bg-gray-50 border-b border-gray-200 px-5 py-3">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Organization Details</h3>
            {isOrgAdmin && (
              !isEditingOrg ? (
                <button
                  onClick={() => setIsEditingOrg(true)}
                  className="btn-app btn-app--primary"
                >
                  Edit
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveOrgName}
                    disabled={isSavingOrg}
                    className="btn-app btn-app--primary"
                  >
                    {isSavingOrg ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingOrg(false);
                      setEditedOrgName(organization.name);
                      setOrgEditError('');
                    }}
                    disabled={isSavingOrg}
                    className="btn-app btn-app--neutral"
                  >
                    Cancel
                  </button>
                </div>
              )
            )}
          </div>

          <div className="p-5">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {error}
            </div>
          )}

          {orgEditError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {orgEditError}
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Name</label>
                {isEditingOrg ? (
                  <input
                    type="text"
                    value={editedOrgName}
                    onChange={(e) => setEditedOrgName(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={isSavingOrg}
                    autoFocus
                  />
                ) : (
                  <p className="text-gray-900">{organization.name}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Code</label>
                <p className="text-gray-900 font-mono">{organization.code}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Status</label>
                <span className={`inline-block px-2 py-1 text-xs rounded ${
                  organization.is_active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {organization.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              {organization.user_count !== undefined && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Users</label>
                  <p className="text-gray-900">{organization.user_count}</p>
                </div>
              )}
              {organization.admin_count !== undefined && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Admins</label>
                  <p className="text-gray-900">{organization.admin_count}</p>
                </div>
              )}
            </div>
            {organization.created_at && (
              <div>
                <label className="block text-sm font-medium text-gray-500">Created</label>
                <p className="text-gray-900 text-sm">
                  {new Date(organization.created_at).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
          </div>
        </div>
      </div>

      {/* Right Column: Users List */}
      <div>
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="flex justify-between items-center bg-gray-50 border-b border-gray-200 px-5 py-3">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">Users</h3>
              {isOrgAdmin && (
                <button
                  onClick={() => {
                    setEditingUser(undefined);
                    setShowUserForm(true);
                  }}
                  className="btn-app btn-app--primary"
                >
                  Add User
                </button>
              )}
          </div>

          {/* Search and Filters */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex flex-wrap gap-3">
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 min-w-[120px] px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="member">Member</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {usersError && (
            <div className="m-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {usersError}
            </div>
          )}

          {isLoadingUsers ? (
            <div className="p-6 text-center text-gray-500">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              {users.length === 0 ? 'No users in this organization' : 'No users match the filters'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Username
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredUsers.map((orgUser) => (
                    <tr
                      key={orgUser.id}
                      onClick={isOrgAdmin ? () => handleUserClick(orgUser) : undefined}
                      className={isOrgAdmin ? "cursor-pointer hover:bg-gray-50" : ""}
                    >
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">{orgUser.username}</div>
                        <div className="text-xs text-gray-500">{orgUser.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-1 text-xs rounded ${
                          orgUser.organization_role?.toLowerCase() === 'admin'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {orgUser.organization_role || 'Member'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-1 text-xs rounded ${
                          orgUser.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {orgUser.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="px-4 py-3 border-t border-gray-200 text-sm text-gray-500">
            {filteredUsers.length} of {users.length} users
          </div>
        </div>
      </div>

      {/* User Form Modal */}
      {showUserForm && (
        <UserForm
          user={editingUser}
          organizationId={user?.current_organization?.id}
          onSuccess={handleUserFormSuccess}
          onCancel={() => {
            setShowUserForm(false);
            setEditingUser(undefined);
          }}
        />
      )}
      </div>
    </div>
  );
}
